import { Button, Tooltip } from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { Eye, Trash2, Undo2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CancelOrderModal from "../modal/cancel-order";
import { OrderRefundModal } from "../modal/refund";

import type { DataGridColumn } from "@/components/kit";
import type { OrderFilterValues } from "@/pages/orders";

import {
  AttributionFilterSelects,
  SourceCell,
  appliedAttributionFilters,
} from "@/components/attribution";
import {
  AppliedFilters,
  ConfirmDialog,
  DataTable,
  FilterSegment,
  FilterSelect,
  FiltersPopover,
  SearchBox,
  notify,
} from "@/components/kit";
import {
  DeliveryTypeChip,
  PersonCell,
  StatusChip,
  Toolbar,
  aed,
  formatDay,
} from "@/components/sales/shared";
import { useAttributionOptions } from "@/hooks/api/order/useAttributionOptions";
import { useCancelOrder } from "@/hooks/api/order/useCancelOrder";
import { useDeleteOrder } from "@/hooks/api/order/useDeleteOrder";
import { DateFilterType } from "@/enums/date";
import { OrderStatus } from "@/enums/order";
import { PaymentStatus } from "@/enums/payment";
import { OrderWithRelations } from "@/types/order";

interface OrderListTableProps {
  items: OrderWithRelations[];
  page: number;
  totalOrders: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  limit: number;
  onLimitChange: (limit: number) => void;
  search: string;
  onSearchChange: (search: string) => void;
  filters: OrderFilterValues;
  onFiltersChange: (filters: OrderFilterValues) => void;
}

/** The statuses shown as tabs; the rest sit under "Other status" in the Filters popover. */
const TAB_STATUSES = [
  OrderStatus.PENDING,
  OrderStatus.PAID,
  OrderStatus.PACKAGED,
  OrderStatus.ENROUTE,
  OrderStatus.DELIVERED,
];
const OTHER_STATUSES = [
  OrderStatus.AWAITING_PAYMENT,
  OrderStatus.FAILED,
  OrderStatus.CANCELLED,
];

const STATUS_LABEL: Record<string, string> = {
  [OrderStatus.ENROUTE]: "En route",
  [OrderStatus.AWAITING_PAYMENT]: "Awaiting payment",
};
const statusLabel = (status: string) =>
  STATUS_LABEL[status] ?? status.charAt(0).toUpperCase() + status.slice(1);

const DATE_LABEL: Record<string, string> = {
  [DateFilterType.LAST_7_DAYS]: "7 days",
  [DateFilterType.LAST_15_DAYS]: "15 days",
  [DateFilterType.LAST_30_DAYS]: "30 days",
};

const canCancel = (status: string) =>
  status !== OrderStatus.CANCELLED &&
  status !== OrderStatus.DELIVERED &&
  status !== OrderStatus.FAILED;

/** The API only deletes orders that can no longer move money. */
const canDelete = (status: string) =>
  status === OrderStatus.CANCELLED || status === OrderStatus.FAILED;

export const OrderListTable = ({
  items,
  page,
  totalOrders,
  changePage,
  isLoading,
  limit,
  onLimitChange,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
}: OrderListTableProps) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<OrderFilterValues>(filters);
  const [refundOrder, setRefundOrder] = useState<OrderWithRelations | null>(
    null,
  );
  const [cancelTarget, setCancelTarget] = useState<OrderWithRelations | null>(
    null,
  );
  const { mutate: cancelOrder, isPending: isCancelling } = useCancelOrder();
  const [deleteTarget, setDeleteTarget] = useState<OrderWithRelations | null>(
    null,
  );
  const { mutate: deleteOrder, isPending: isDeleting } = useDeleteOrder();
  const { data: attributionOptions } = useAttributionOptions();

  const handleCancelOrder = () => {
    if (!cancelTarget) return;
    cancelOrder(cancelTarget.id, {
      onSuccess: () => {
        notify.success(
          "Order Cancelled",
          "Order has been cancelled successfully",
        );
        setCancelTarget(null);
      },
      onError: () => notify.error("Failed to cancel order", "Please try again"),
    });
  };

  const handleDeleteOrder = () => {
    if (!deleteTarget) return;
    deleteOrder(deleteTarget.id, {
      onSuccess: () => {
        notify.success("Order Deleted", "Order has been deleted successfully");
        setDeleteTarget(null);
      },
      onError: () => notify.error("Failed to delete order", "Please try again"),
    });
  };

  const columns = useMemo<DataGridColumn<OrderWithRelations>[]>(
    () => [
      {
        id: "displayId",
        header: "Order",
        isRowHeader: true,
        width: 110,
        cell: (order) => (
          <span className="font-medium tabular-nums">#{order.displayId}</span>
        ),
      },
      {
        id: "customer",
        header: "Customer",
        minWidth: 240,
        cell: (order) => (
          <PersonCell
            firstName={order.user.firstName}
            lastName={order.user.lastName}
            phoneNumber={order.user.phoneNumber}
          />
        ),
      },
      {
        id: "totalAmount",
        header: "Total",
        align: "end",
        cell: (order) => (
          <span className="font-medium tabular-nums">
            {aed(order.totalAmount)}
          </span>
        ),
      },
      {
        id: "paymentStatus",
        header: "Payment",
        cell: (order) => (
          <StatusChip
            color={order.paymentIntentId ? "success" : "default"}
            label={order.paymentIntentId ? "Paid" : "Pending"}
            status={order.paymentIntentId ? "paid" : "pending"}
          />
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: (order) => <StatusChip status={order.status} />,
      },
      {
        id: "orderDate",
        header: "Placed",
        cell: (order) => (
          <span className="tabular-nums">{formatDay(order.createdAt)}</span>
        ),
      },
      {
        id: "deliveryDate",
        header: "Delivery",
        cell: (order) =>
          order.deliveryDate ? (
            <span className="tabular-nums">
              {formatDay(order.deliveryDate)}
            </span>
          ) : (
            <span className="text-muted">Not set</span>
          ),
      },
      {
        id: "deliveryType",
        header: "Type",
        cell: (order) =>
          order.deliveryType ? (
            <DeliveryTypeChip type={order.deliveryType} />
          ) : (
            <span className="text-muted">Not set</span>
          ),
      },
      {
        id: "source",
        header: "Source",
        minWidth: 140,
        cell: (order) => <SourceCell attribution={order.attribution} />,
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "end",
        width: 132,
        cell: (order) => (
          <div className="flex items-center justify-end gap-0.5">
            <Tooltip delay={400}>
              <Button
                isIconOnly
                aria-label="Refund"
                size="sm"
                variant="ghost"
                onPress={() => setRefundOrder(order)}
              >
                <Undo2 className="size-4" />
              </Button>
              <Tooltip.Content>Refund</Tooltip.Content>
            </Tooltip>
            <Tooltip delay={400}>
              <Button
                isIconOnly
                aria-label="View Order"
                size="sm"
                variant="ghost"
                onPress={() => navigate(`/orders/${order.id}`)}
              >
                <Eye className="size-4" />
              </Button>
              <Tooltip.Content>View order</Tooltip.Content>
            </Tooltip>
            {canCancel(order.status) ? (
              <Tooltip delay={400}>
                <Button
                  isIconOnly
                  aria-label="Cancel Order"
                  className="text-(--danger)"
                  size="sm"
                  variant="ghost"
                  onPress={() => setCancelTarget(order)}
                >
                  <X className="size-4" />
                </Button>
                <Tooltip.Content>Cancel order</Tooltip.Content>
              </Tooltip>
            ) : canDelete(order.status) ? (
              <Tooltip delay={400}>
                <Button
                  isIconOnly
                  aria-label="Delete Order"
                  className="text-(--danger)"
                  size="sm"
                  variant="ghost"
                  onPress={() => setDeleteTarget(order)}
                >
                  <Trash2 className="size-4" />
                </Button>
                <Tooltip.Content>Delete order</Tooltip.Content>
              </Tooltip>
            ) : (
              <span aria-hidden="true" className="size-8" />
            )}
          </div>
        ),
      },
    ],
    [navigate],
  );

  const applied = [
    filters.orderStatus && !TAB_STATUSES.includes(filters.orderStatus as never)
      ? {
          id: "orderStatus",
          label: "Status",
          value: statusLabel(filters.orderStatus),
        }
      : null,
    filters.paymentStatus
      ? {
          id: "paymentStatus",
          label: "Payment",
          value: statusLabel(filters.paymentStatus),
        }
      : null,
    filters.dateFilter
      ? {
          id: "dateFilter",
          label: "Placed",
          value: `Last ${DATE_LABEL[filters.dateFilter] ?? filters.dateFilter}`,
        }
      : null,
    ...appliedAttributionFilters(filters),
  ].filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  const apply = (next: OrderFilterValues) => {
    setDraft(next);
    onFiltersChange(next);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar
        end={
          <>
            <SearchBox
              className="w-80"
              placeholder="Search name, ID or phone"
              value={search}
              onChange={onSearchChange}
            />
            <FiltersPopover
              activeCount={applied.length}
              onApply={() => onFiltersChange(draft)}
              onClearAll={() => apply({ orderStatus: filters.orderStatus })}
            >
              <FilterSelect
                allLabel="None"
                label="Other status"
                options={OTHER_STATUSES.map((status) => ({
                  id: status,
                  label: statusLabel(status),
                }))}
                value={
                  draft.orderStatus &&
                  OTHER_STATUSES.includes(draft.orderStatus as never)
                    ? draft.orderStatus
                    : null
                }
                onChange={(value) =>
                  setDraft({ ...draft, orderStatus: value ?? undefined })
                }
              />
              <FilterSelect
                label="Payment"
                options={Object.values(PaymentStatus).map((status) => ({
                  id: status,
                  label: statusLabel(status),
                }))}
                value={draft.paymentStatus ?? null}
                onChange={(value) =>
                  setDraft({ ...draft, paymentStatus: value ?? undefined })
                }
              />
              <FilterSegment
                label="Placed"
                options={[
                  { id: "", label: "Any time" },
                  ...Object.values(DateFilterType).map((value) => ({
                    id: value,
                    label: DATE_LABEL[value] ?? value,
                  })),
                ]}
                value={draft.dateFilter ?? ""}
                onChange={(value) =>
                  setDraft({ ...draft, dateFilter: value || undefined })
                }
              />
              <AttributionFilterSelects
                options={attributionOptions}
                value={draft}
                onChange={setDraft}
              />
            </FiltersPopover>
          </>
        }
      >
        <div className="isolate max-w-full overflow-x-auto">
          <Segment
            aria-label="Order status"
            selectedKey={
              filters.orderStatus &&
              TAB_STATUSES.includes(filters.orderStatus as never)
                ? filters.orderStatus
                : filters.orderStatus
                  ? ""
                  : "all"
            }
            onSelectionChange={(key) =>
              apply({
                ...filters,
                orderStatus: key === "all" ? undefined : String(key),
              })
            }
          >
            <Segment.Item id="all">All</Segment.Item>
            {TAB_STATUSES.map((status) => (
              <Segment.Item key={status} id={status}>
                <Segment.Separator />
                {statusLabel(status)}
              </Segment.Item>
            ))}
          </Segment>
        </div>
        <AppliedFilters
          filters={applied}
          onClearAll={() => apply({ orderStatus: filters.orderStatus })}
          onRemove={(id) =>
            apply({ ...filters, [id]: undefined } as OrderFilterValues)
          }
        />
      </Toolbar>

      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No orders found",
          description: "Try a different search or clear the filters.",
        }}
        getRowId={(order) => order.id}
        isLoading={isLoading}
        label="Orders"
        pagination={{
          page,
          pageSize: limit,
          total: totalOrders,
          onPageChange: changePage,
          itemLabel: "orders",
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 20, 50, 100],
        }}
        rows={items}
      />

      {refundOrder ? (
        <OrderRefundModal
          displayId={refundOrder.displayId}
          isOpen
          items={refundOrder.items.map((item) => ({
            id: item.id,
            name: item.productName,
            status: item.orderItemStatus,
            quantity: item.quantity,
            price: item.unitPrice,
          }))}
          orderId={refundOrder.id}
          totalAmount={refundOrder.totalAmount}
          onClose={() => setRefundOrder(null)}
        />
      ) : null}

      {cancelTarget ? (
        <CancelOrderModal
          displayId={cancelTarget.displayId}
          handleCancel={handleCancelOrder}
          isLoading={isCancelling}
          isOpen
          totalAmount={cancelTarget.totalAmount}
          onClose={() => setCancelTarget(null)}
        />
      ) : null}

      {deleteTarget ? (
        <ConfirmDialog
          isOpen
          cancelLabel="Keep Order"
          confirmLabel="Delete Order"
          isPending={isDeleting}
          title={`Delete order #${deleteTarget.displayId}?`}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteOrder}
        >
          <p className="text-muted text-sm">
            The order will be removed from the orders list and the
            customer&apos;s order history.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
};
